import {
  ActionCreatorWithOptionalPayload,
  ListenerEffectAPI,
  ThunkDispatch,
  UnknownAction,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { RootState } from 'redux/reemaStore';

// HACK: This is a workaround because the AsyncThunkFulfilledActionCreator type is actually no longer exported from Redux toolkit
type AsyncThunkFulfilledActionCreator<Payload, ThunkArg = void> = (
  payload: Payload,
  requestId: string,
  arg: ThunkArg
) => {
  type: string;
  payload: Payload;
  meta: {
    requestId: string;
    arg: ThunkArg;
    requestStatus: 'fulfilled';
  };
};

// Middleware for handling side effects
// Created in the global scope so that it can be used in multiple slices
export const carePortalMiddleware = createListenerMiddleware();

type ActionCreatorPayload<T> =
  | ActionCreatorWithOptionalPayload<T, string>
  | AsyncThunkFulfilledActionCreator<T, unknown>;

// Type the listener effect API to include RootState
// This is a workaround for the fact that the listener effect API has unknown state type.
// This shows the correct state when using the addEffect function.
type TypedListenerEffectAPI = ListenerEffectAPI<
  RootState,
  ThunkDispatch<unknown, unknown, UnknownAction>,
  unknown
>;

interface EffectBuilderDefinition<T> {
  action: { payload: T; type: string };
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
  state: RootState;
  listenerApi: TypedListenerEffectAPI;
}
interface EffectDefinition<T> {
  action?: ActionCreatorPayload<T>;
  actions?: Array<ActionCreatorPayload<any>>;
  effect: (effectBuilder: EffectBuilderDefinition<T>) => void;
}

export const addToEffectsMiddleware = (
  configFunc: (builder: <T>(effect: EffectDefinition<T>) => void) => void
): void => {
  const addEffect = <T>(effect: EffectDefinition<T>) => {
    carePortalMiddleware.startListening({
      matcher: isAnyOf(
        ...((effect.actions || [effect.action]) as Array<
          ActionCreatorWithOptionalPayload<T>
        >)
      ),
      effect: (action: { payload: T; type: string }, listenerApi) => {
        const dispatch = listenerApi.dispatch;
        const state = listenerApi.getState() as RootState;
        // Can cancel other running instances
        listenerApi.cancelActiveListeners();

        effect.effect({
          action,
          dispatch,
          state,
          listenerApi: listenerApi as TypedListenerEffectAPI,
        });
      },
    });
  };

  configFunc(addEffect);
};
